import type { ChangelogEntry } from 'src/stores/changelog/interfaces';

import { defineStore } from 'pinia';
import { LAST_SEEN_CHANGELOG_VERSION, LAST_SEEN_UNRELEASED_FINGERPRINT } from 'src/stores/changelog/localStorageKeys';
import createDefaultChangelogState from 'src/stores/changelog/state';

function compareVersions(a: string, b: string): number {
	if (a === b) {
		return 0;
	}
	const partsA = a.split('.').map((part) => Number.parseInt(part, 10));
	const partsB = b.split('.').map((part) => Number.parseInt(part, 10));
	const length = Math.max(partsA.length, partsB.length);
	for (let i = 0; i < length; i += 1) {
		const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0);
		if (diff !== 0) {
			return diff > 0 ? 1 : -1;
		}
	}
	return 0;
}

function getLatestRealVersion(entries: ChangelogEntry[]): null | string {
	const real = entries.filter((entry) => entry.version !== 'unreleased');
	if (real.length === 0) {
		return null;
	}
	return real.reduce(
		(best, entry) => (compareVersions(entry.version, best) > 0 ? entry.version : best),
		real[0].version
	);
}

function computeUnreleasedFingerprint(entries: ChangelogEntry[]): string {
	const unreleased = entries.find((entry) => entry.version === 'unreleased');
	if (!unreleased) {
		return '';
	}
	return `${unreleased.de.join('§')}|${unreleased.en.join('§')}`;
}

export const useChangelogStore = defineStore('changelog', {
	actions: {
		applyEntries(entries: ChangelogEntry[]): void {
			this.entries = entries;
			this.isLoaded = true;
			if (entries.length === 0) {
				return;
			}
			if (this.lastSeenVersion === null) {
				const latest = getLatestRealVersion(entries);
				if (latest !== null) {
					this.lastSeenVersion = latest;
					localStorage.setItem(LAST_SEEN_CHANGELOG_VERSION, latest);
					const fingerprint = computeUnreleasedFingerprint(entries);
					this.lastSeenUnreleasedFingerprint = fingerprint;
					localStorage.setItem(LAST_SEEN_UNRELEASED_FINGERPRINT, fingerprint);
				}
			}
		},
		async fetchChangelog(): Promise<void> {
			try {
				const cacheBustTag = typeof __APP_VERSION__ === 'undefined' ? 'dev' : __APP_VERSION__;
				const response = await fetch(`/changelog.json?v=${cacheBustTag}`);
				if (!response.ok) {
					this.isLoaded = true;
					return;
				}
				const payload = (await response.json()) as ChangelogEntry[];
				if (!Array.isArray(payload)) {
					this.isLoaded = true;
					return;
				}
				this.applyEntries(payload);
			} catch {
				this.isLoaded = true;
			}
		},
		markAsSeen(): void {
			const latest = getLatestRealVersion(this.entries);
			if (latest !== null) {
				this.lastSeenVersion = latest;
				localStorage.setItem(LAST_SEEN_CHANGELOG_VERSION, latest);
			}
			const fingerprint = computeUnreleasedFingerprint(this.entries);
			this.lastSeenUnreleasedFingerprint = fingerprint;
			localStorage.setItem(LAST_SEEN_UNRELEASED_FINGERPRINT, fingerprint);
		},
	},
	getters: {
		hasUnseenEntries(state): boolean {
			if (state.entries.length === 0) {
				return false;
			}
			if (state.lastSeenVersion === null) {
				return state.entries.some((entry) => entry.version === 'unreleased');
			}
			const latestReal = getLatestRealVersion(state.entries);
			if (latestReal !== null && compareVersions(latestReal, state.lastSeenVersion) > 0) {
				return true;
			}
			const hasUnreleased = state.entries.some((entry) => entry.version === 'unreleased');
			if (hasUnreleased) {
				const currentFingerprint = computeUnreleasedFingerprint(state.entries);
				return currentFingerprint !== (state.lastSeenUnreleasedFingerprint ?? '');
			}
			return false;
		},
		seenEntries(state): ChangelogEntry[] {
			if (state.lastSeenVersion === null) {
				return state.entries.filter((entry) => entry.version !== 'unreleased');
			}
			const lastSeen = state.lastSeenVersion;
			const currentFingerprint = computeUnreleasedFingerprint(state.entries);
			const unreleasedSeen = currentFingerprint === (state.lastSeenUnreleasedFingerprint ?? '');
			return state.entries.filter((entry) => {
				if (entry.version === 'unreleased') {
					return unreleasedSeen;
				}
				return compareVersions(entry.version, lastSeen) <= 0;
			});
		},
		unseenEntries(state): ChangelogEntry[] {
			if (state.lastSeenVersion === null) {
				return [];
			}
			const lastSeen = state.lastSeenVersion;
			const currentFingerprint = computeUnreleasedFingerprint(state.entries);
			const unreleasedSeen = currentFingerprint === (state.lastSeenUnreleasedFingerprint ?? '');
			return state.entries.filter((entry) => {
				if (entry.version === 'unreleased') {
					return !unreleasedSeen;
				}
				return compareVersions(entry.version, lastSeen) > 0;
			});
		},
	},
	state: createDefaultChangelogState,
});
export * from './interfaces';
export * from './types';
